// src/components/UsuariosPermissoes.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import TrialCountdown from './TrialCountdown';
import { utcToSpInput, spInputToUtcIso } from '../utils/datetime';
import {
  FaUserEdit, FaClock, FaSms, FaIdCard, FaMicrosoft,
  FaGoogle, FaLock, FaUserShield, FaKey, FaTimes, FaCheck,
  FaRedoAlt, FaRocket, FaSignOutAlt, FaCalendarAlt,
  FaUserSecret, FaCommentAlt, FaTrash, FaShieldAlt,
  FaBan, FaCheckCircle, FaExclamationTriangle, FaDesktop,
  FaEye, FaEyeSlash, FaCopy, FaDice, FaMobileAlt,
  // ── NOVOS ──────────────────────────────────────────────────────────
  FaNetworkWired, FaPlus, FaBrain, FaChartBar, FaHistory,
  FaFire, FaVideo, FaMousePointer, FaChartLine, FaSync,
  FaInfoCircle, FaShieldVirus, FaToggleOn, FaToggleOff,
  FaHourglassHalf, FaMapMarkerAlt, FaFingerprint, FaBolt,
  FaClipboardCheck,
} from 'react-icons/fa';

/* ================================================================================================
 * Utils
 * ================================================================================================ */
const normalizeEmail = (v = '') => v.trim().toLowerCase();
const maskCPF = (v = '') =>
  v.replace(/\D/g, '').slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
const unmaskCPF = (v = '') => v.replace(/\D/g, '').slice(0, 11);
const validateCPF = (cpf) => {
  const c = unmaskCPF(cpf);
  if (!c || c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(c[i], 10) * (10 - i);
  let d1 = (sum * 10) % 11; if (d1 === 10) d1 = 0;
  if (d1 !== parseInt(c[9], 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(c[i], 10) * (11 - i);
  let d2 = (sum * 10) % 11; if (d2 === 10) d2 = 0;
  return d2 === parseInt(c[10], 10);
};
const maskPhoneBR = (v = '') => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};
const unmaskPhone = (v = '') => v.replace(/\D/g, '').slice(0, 11);
const isValidEmail = (email = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
const isTimeBefore = (t1, t2) => (!t1 || !t2) ? true : t1 < t2;
const sortUnique = (arr = []) => Array.from(new Set(arr)).sort((a, b) => a - b);
const isDefined = (v) => typeof v !== 'undefined';
const fmtDate = (iso) => iso ? new Intl.DateTimeFormat('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }).format(new Date(iso.toString().replace(' ', 'T'))) : '—';
const fmtRelative = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso.toString().replace(' ', 'T'));
  const min  = Math.floor(diff / 60000);
  const h    = Math.floor(min / 60);
  const d    = Math.floor(h / 24);
  if (d > 0)  return `há ${d}d`;
  if (h > 0)  return `há ${h}h`;
  if (min > 0) return `há ${min}min`;
  return 'agora';
};

/* ── Regras de senha ──────────────────────────────────────────────────────── */
const SYMBOL_CHARS = '@$!%*?&#+^~';
function getPasswordRules(p = '') {
  return [
    { key: 'len',    ok: p.length >= 12,               label: '12 caracteres' },
    { key: 'upper',  ok: /[A-Z]/.test(p),              label: 'Maiúscula' },
    { key: 'lower',  ok: /[a-z]/.test(p),              label: 'Minúscula' },
    { key: 'num',    ok: /\d/.test(p),                 label: 'Número' },
    { key: 'symbol', ok: new RegExp(`[${SYMBOL_CHARS.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(p), label: 'Símbolo' },
  ];
}
function isPasswordValid(p = '') { return getPasswordRules(p).every(r => r.ok); }
function generateStrongPassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const nums  = '23456789';
  const syms  = SYMBOL_CHARS;
  const all   = upper + lower + nums + syms;
  const req   = [upper,lower,nums,syms].map(s => s[Math.floor(Math.random()*s.length)]);
  const extra = Array.from({ length: 10 }, () => all[Math.floor(Math.random()*all.length)]);
  return [...req,...extra].sort(() => Math.random()-.5).join('');
}

/* ── Inteligência: heatmap ────────────────────────────────────────────────── */
const buildHeatmap = (logs = []) => {
  const matrix = Array.from({ length: 7 }, () => Array(24).fill(0));
  let maxVal = 0;
  logs.forEach(log => {
    if (!log.created_at) return;
    const d = new Date(log.created_at.toString().replace(' ', 'T'));
    if (isNaN(d)) return;
    const day = d.getDay();
    const hour = d.getHours();
    matrix[day][hour]++;
    maxVal = Math.max(maxVal, matrix[day][hour]);
  });
  return { matrix, maxVal };
};

/* ── Inteligência: risk score ─────────────────────────────────────────────── */
const calcRiskScore = (userData, logs = []) => {
  const factors = [];
  let score = 0;
  const add = (label, pts, severity, icon) => { score += pts; factors.push({ label, pts, severity, icon }); };

  if (!userData?.mfa_enabled)
    add('MFA desabilitado — risco de acesso não autorizado', 30, 'HIGH', FaLock);
  if ((userData?.sso_google || userData?.sso_microsoft) && userData?.login_local_enabled)
    add('Login local ativo junto com SSO — bypass potencial', 20, 'MEDIUM', FaShieldVirus);
  if (!userData?.regras_acesso?.ips_permitidos?.length)
    add('Sem restrição de IP de origem', 10, 'LOW', FaNetworkWired);
  if (!userData?.regras_acesso?.timeout_inatividade_min)
    add('Sem timeout de inatividade configurado', 5, 'LOW', FaHourglassHalf);

  if (userData?.ultimo_acesso) {
    const daysSince = (Date.now() - new Date(userData.ultimo_acesso.toString().replace(' ','T'))) / 86400000;
    if (daysSince > 60) add(`Conta inativa há ${Math.floor(daysSince)} dias — conta fantasma?`, 25, 'MEDIUM', FaDesktop);
  } else {
    add('Nunca acessou o sistema', 15, 'LOW', FaDesktop);
  }

  const failedLogins = logs.filter(l => (l.acao||'').includes('LOGIN_FALHA')).length;
  if (failedLogins >= 5) add(`${failedLogins} tentativas de login falhadas`, 25, 'HIGH', FaBolt);
  else if (failedLogins > 0) add(`${failedLogins} tentativa(s) de login falhada(s)`, 8, 'LOW', FaBolt);

  const forceLogoffs = logs.filter(l => (l.acao||'').includes('LOGOFF_FORCADO')).length;
  if (forceLogoffs > 0) add(`${forceLogoffs} logoff(s) forçado(s) registrado(s)`, 15, 'MEDIUM', FaSignOutAlt);

  const pwdChanges = logs.filter(l => (l.acao||'').includes('SENHA')).length;
  if (pwdChanges > 5) add(`${pwdChanges} trocas de senha — comportamento incomum`, 10, 'MEDIUM', FaKey);

  const finalScore = Math.min(100, score);
  return {
    score: finalScore,
    level: finalScore >= 60 ? 'CRÍTICO' : finalScore >= 35 ? 'MÉDIO' : 'BAIXO',
    color: finalScore >= 60 ? '#dc2626' : finalScore >= 35 ? '#f59e0b' : '#16a34a',
    bg: finalScore >= 60 ? '#fff1f0' : finalScore >= 35 ? '#fff7ed' : '#f0fdf4',
    factors,
  };
};

/* ── Inteligência: ícone/cor por ação ────────────────────────────────────── */
const ACTION_MAP = {
  LOGIN:             { icon: FaCheck,       color: '#16a34a', bg: '#f0fdf4', label: 'Login'              },
  LOGIN_FALHA:       { icon: FaBan,         color: '#dc2626', bg: '#fff1f0', label: 'Falha de Login'      },
  LOGOUT:            { icon: FaSignOutAlt,  color: '#64748b', bg: '#f8fafc', label: 'Logout'              },
  LOGOFF_FORCADO:    { icon: FaBolt,        color: '#f59e0b', bg: '#fff7ed', label: 'Logoff Forçado'      },
  SENHA:             { icon: FaKey,         color: '#7c3aed', bg: '#f5f3ff', label: 'Senha Alterada'      },
  USUARIO_CRIADO:    { icon: FaUserEdit,    color: '#2563eb', bg: '#eff6ff', label: 'Criação de Usuário'  },
  USUARIO_ATUALIZADO:{ icon: FaUserEdit,    color: '#0891b2', bg: '#ecfeff', label: 'Atualização'         },
  USUARIO_EXCLUIDO:  { icon: FaTrash,       color: '#dc2626', bg: '#fff1f0', label: 'Exclusão'            },
  MFA:               { icon: FaFingerprint, color: '#9333ea', bg: '#faf5ff', label: 'Evento MFA'          },
  IMPERSONACAO:      { icon: FaUserSecret,  color: '#b45309', bg: '#fff7ed', label: 'Impersonação'        },
};
const getAction = (acao = '') => {
  const key = Object.keys(ACTION_MAP).find(k => acao.toUpperCase().includes(k));
  return ACTION_MAP[key] || { icon: FaHistory, color: '#475569', bg: '#f8fafc', label: acao };
};

/* ── Validação básica de IP/CIDR ──────────────────────────────────────────── */
const isValidIpOrCidr = (val) => {
  const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
  const ipv6  = /^[a-fA-F0-9:]+$/;
  return ipv4.test(val.trim()) || ipv6.test(val.trim());
};

/* ================================================================================================
 * COMPONENTE 1: MODAL SIMPLES (ADMIN cliente)
 * ================================================================================================ */
export function SimpleEditModal({ usuario, onClose, onUpdate }) {
  const [editData, setEditData] = useState(usuario || {});
  const [originalData]          = useState(usuario || {});
  const [saving, setSaving]     = useState(false);
  const modalRef                = useRef(null);
  const firstInputRef           = useRef(null);

  useEffect(() => { setEditData(usuario || {}); }, [usuario]);
  useEffect(() => {
    firstInputRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleCloseGuard();
      if (e.key === 'Tab' && modalRef.current) {
        const list = getFocusableElements(modalRef.current);
        if (!list.length) return;
        const first = list[0], last = list[list.length - 1];
        if (!e.shiftKey && document.activeElement === last)      { first.focus(); e.preventDefault(); }
        else if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line
  }, []);

  const isDirty = useMemo(() => JSON.stringify(editData) !== JSON.stringify(originalData), [editData, originalData]);
  const handleCloseGuard = () => {
    if (isDirty && !window.confirm('Existem alterações não salvas. Deseja sair mesmo assim?')) return;
    onClose?.();
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'email')    v = normalizeEmail(v);
    if (name === 'telefone') v = maskPhoneBR(v);
    setEditData(prev => ({ ...prev, [name]: v }));
  };
  const validate = () => {
    if (!editData.nome?.trim())        { toast.warning('Informe o nome completo.'); return false; }
    if (!isValidEmail(editData.email)) { toast.warning('E-mail inválido.'); return false; }
    return true;
  };
  const handleUpdate = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    try {
      setSaving(true);
      await api.put(`/api/usuarios/${editData.id}`, {
        nome: editData.nome.trim(),
        email: normalizeEmail(editData.email || ''),
        telefone: unmaskPhone(editData.telefone || ''),
        perfil: editData.perfil || 'operacional',
      });
      toast.success('Cadastro básico atualizado.');
      onUpdate?.(); onClose?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao atualizar.');
    } finally { setSaving(false); }
  };

  return (
    <div className="blb-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target.classList.contains('blb-overlay')) handleCloseGuard(); }}>
      <div className="blb-modal simple fade-in" ref={modalRef} aria-labelledby="simple-edit-title">
        <div className="blb-modal-header">
          <div className="header-title">
            <div className="icon-badge"><FaUserEdit /></div>
            <div><h3 id="simple-edit-title">Edição Rápida</h3><span>Dados cadastrais fundamentais</span></div>
          </div>
          <button onClick={handleCloseGuard} className="btn-close" aria-label="Fechar"><FaTimes /></button>
        </div>
        <form onSubmit={handleUpdate} className="blb-modal-body">
          <div className="blb-form-group">
            <label>Nome Completo</label>
            <input ref={firstInputRef} type="text" name="nome" value={editData.nome || ''} onChange={handleChange} required />
          </div>
          <div className="blb-grid-2">
            <div className="blb-form-group">
              <label>E-mail</label>
              <input type="email" name="email" value={editData.email || ''} onChange={handleChange} required />
            </div>
            <div className="blb-form-group">
              <label>Perfil</label>
              <select name="perfil" value={editData.perfil || 'operacional'} onChange={handleChange}>
                <option value="operacional">Operacional</option>
                <option value="financeiro">Financeiro</option>
                <option value="rh">RH</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
          </div>
          <div className="blb-form-group">
            <label>Telefone / WhatsApp</label>
            <input type="text" name="telefone" value={editData.telefone || ''} onChange={handleChange} inputMode="numeric" placeholder="(11) 91234-5678" />
          </div>
        </form>
        <div className="blb-modal-footer">
          <button onClick={handleCloseGuard} className="btn-ghost">Cancelar</button>
          <button onClick={handleUpdate} className="btn-primary" disabled={saving}>{saving ? 'SALVANDO…' : 'SALVAR DADOS'}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Acessibilidade: trap de foco ─────────────────────────────────────────── */
function getFocusableElements(container) {
  if (!container) return [];
  const sel = ['a[href]','area[href]','input:not([disabled])','select:not([disabled])','textarea:not([disabled])','button:not([disabled])','iframe','[contenteditable="true"]','[tabindex]:not([tabindex="-1"])'];
  return Array.from(container.querySelectorAll(sel.join(','))).filter(el => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
}

/* ================================================================================================
 * COMPONENTE 2: MODAL AVANÇADO DE GOVERNANÇA (ADMIN GERAL)
 * ================================================================================================ */
const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const TIMEOUT_OPTIONS = [
  { value: '',    label: 'Desabilitado — sem expiração por inatividade' },
  { value: '15',  label: '15 minutos'  },
  { value: '30',  label: '30 minutos'  },
  { value: '60',  label: '1 hora'      },
  { value: '120', label: '2 horas'     },
  { value: '240', label: '4 horas'     },
  { value: '480', label: '8 horas'     },
];

const S = {
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 },
  card: (accent = '#e2e8f0') => ({
    background: '#fff',
    border: `1px solid ${accent}`,
    borderLeft: `4px solid ${accent}`,
    borderRadius: 10,
    padding: '16px 18px',
    color: '#0f172a',
    boxShadow: '0 1px 4px rgba(0,0,0,.04)',
  }),
  cardLabel: {
    fontSize: 10, fontWeight: 800, letterSpacing: 1.2,
    textTransform: 'uppercase', color: '#64748b', marginBottom: 10,
    display: 'flex', alignItems: 'center', gap: 6,
  },
  pill: (color, bg) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 11, fontWeight: 700,
    padding: '3px 10px', borderRadius: 99,
    color, background: bg,
  }),
  divider: {
    fontSize: 10, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase',
    color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 10,
    margin: '22px 0 14px',
  },
  dividerLine: { flex: 1, height: 1, background: '#e2e8f0' },
  actionRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  btn: (variant = 'default') => {
    const variants = {
      default: { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' },
      danger:  { background: '#fff1f0', color: '#dc2626', border: '1px solid #fecaca' },
      warn:    { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' },
      success: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
      purple:  { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' },
      primary: { background: '#0d1b3e', color: '#fff',    border: '1px solid #0d1b3e' },
      cyan:    { background: '#ecfeff', color: '#0891b2', border: '1px solid #a5f3fc' },
    };
    return {
      ...variants[variant],
      padding: '9px 16px', borderRadius: 8, cursor: 'pointer',
      fontSize: 12, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', gap: 7,
      transition: 'filter .15s, transform .1s',
      letterSpacing: .3,
    };
  },
  input: {
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
    padding: '9px 12px', color: '#0f172a', fontSize: 13, outline: 'none',
  },
  textarea: {
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
    padding: '9px 12px', color: '#0f172a', fontSize: 13, outline: 'none',
    resize: 'vertical', minHeight: 72, fontFamily: 'inherit',
  },
  dangerZone: {
    background: '#fff5f5',
    border: '1px solid #fecaca',
    borderLeft: '4px solid #ef4444',
    borderRadius: 10,
    padding: '16px 18px',
    marginTop: 8,
  },
};

export function EditModalAdminGeral({ usuarioId, onClose, onUpdate, allClients = [] }) {
  const [activeTab, setActiveTab]       = useState('dados');
  const [userData, setUserData]         = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  // Senha
  const [newPassword, setNewPassword] = useState('');
  const [showPwd, setShowPwd]         = useState(false);
  const [pwdSaving, setPwdSaving]     = useState(false);

  // MFA setup
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [mfaQrDataUrl, setMfaQrDataUrl] = useState('');
  const [mfaSecret, setMfaSecret]       = useState('');
  const [mfaTotpCode, setMfaTotpCode]   = useState('');
  const [mfaBusy, setMfaBusy]           = useState(false);

  // Features
  const [impersonating, setImpersonating]   = useState(false);
  const [expiryDate, setExpiryDate]         = useState('');
  const [logoutMsg, setLogoutMsg]           = useState('');
  const [sendingLogout, setSendingLogout]   = useState(false);

  // ── NOVO: IP whitelist ──────────────────────────────────────────────────
  const [ipInput, setIpInput] = useState('');

  // ── [TRIAL] Período de teste ────────────────────────────────────────────
  const [trialEnabled, setTrialEnabled]           = useState(false);
  const [trialExpiresLocal, setTrialExpiresLocal] = useState(''); // datetime-local (horário SP)
  const [savingTrial, setSavingTrial]             = useState(false);
  // Trial do CLIENTE (prevalece): quando ligado, o trial unitário fica bloqueado
  const [clienteTrialEnabled, setClienteTrialEnabled]     = useState(false);
  const [clienteTrialExpiresAt, setClienteTrialExpiresAt] = useState(null);

  // ── NOVO: Inteligência ──────────────────────────────────────────────────
  const [auditData, setAuditData]     = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFetched, setAuditFetched] = useState(false);

  const modalRef          = useRef(null);
  const firstFocusableRef = useRef(null);

  /* ─── Fetch principal ─────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/usuarios/${usuarioId}`);
        const base   = (typeof data.regras_acesso === 'object' && data.regras_acesso) || {};
        const regras = {
          // ── CORREÇÃO: padrão = todos os dias, 00:00–23:59 ──────────────
          dias:    Array.isArray(base.dias) && base.dias.length ? base.dias : [0,1,2,3,4,5,6],
          inicio:  base.inicio  || '00:00',
          fim:     base.fim     || '23:59',
          // ── NOVOS CAMPOS ────────────────────────────────────────────────
          ips_permitidos:        Array.isArray(base.ips_permitidos) ? base.ips_permitidos : [],
          timeout_inatividade_min: base.timeout_inatividade_min || null,
        };
        const normalized = {
          ...data,
          telefone:    maskPhoneBR(data.telefone || ''),
          cargo:       data.cargo || '',
          matricula:   data.matricula || '',
          cpf:         maskCPF(data.cpf || ''),
          filial:      data.filial || '',
          centro_custo: data.centroDeCusto || data.centro_custo || '',
          cliente_id:  data.cliente_id ?? '',
          ativo:       data?.status ? data.status === 'ativo' : true,
          regras_acesso: regras,
          sso_google:    !!data.sso_google,
          sso_microsoft: !!data.sso_microsoft,
          mfa_enabled:   !!data.mfa_enabled,
          forcar_troca_senha_90dias: !!data.forcar_troca_senha_90dias,
          liberar_acesso_app: !!data.liberar_acesso_app,
          pode_registrar_os:  !!data.pode_registrar_os,
          email:       normalizeEmail(data.email || ''),
          login_local_enabled: isDefined(data.login_local_enabled) ? !!data.login_local_enabled : true,
          mfa_remember_days: Number.isFinite(Number(data.mfa_remember_days)) ? Number(data.mfa_remember_days) : 7,
          ultimo_acesso: data.ultimo_acesso || null,
          created_at:    data.created_at    || null,
        };
        setUserData(normalized);
        setOriginalData(normalized);
        if (data.data_expiracao_conta) setExpiryDate(data.data_expiracao_conta.slice(0, 10));
        // [TRIAL] popula a partir do backend (trial_expires_at chega em ISO UTC)
        setTrialEnabled(!!data.trial_enabled);
        setTrialExpiresLocal(utcToSpInput(data.trial_expires_at)); // '' se null
        // [TRIAL] estado do trial do CLIENTE (prevalece → trava o unitário)
        setClienteTrialEnabled(!!data.cliente_trial_enabled);
        setClienteTrialExpiresAt(data.cliente_trial_expires_at || null);
      } catch { toast.error('Erro ao carregar governança.'); }
      finally   { setLoading(false); }
    };
    fetchUserData();
  }, [usuarioId]);

  /* ─── Focus trap ──────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!userData) return;
    firstFocusableRef.current?.focus();
    const node = modalRef.current;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleCloseGuard();
      if (e.key === 'Tab' && node) {
        const list = getFocusableElements(node);
        if (!list.length) return;
        const first = list[0], last = list[list.length - 1];
        if (!e.shiftKey && document.activeElement === last)      { first.focus(); e.preventDefault(); }
        else if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line
  }, [userData]);

  /* ─── NOVO: busca auditoria ao abrir a aba ────────────────────────────── */
  const fetchAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const { data } = await api.get(`/api/usuarios/${usuarioId}/audit?limit=150`);
      setAuditData(data);
      setAuditFetched(true);
    } catch {
      setAuditData({ logs: [], user: null, posthog: null });
      setAuditFetched(true);
    } finally { setAuditLoading(false); }
  }, [usuarioId]);

  useEffect(() => {
    if (activeTab === 'inteligencia' && !auditFetched) fetchAudit();
  }, [activeTab, auditFetched, fetchAudit]);

  /* ─── Dirty check ─────────────────────────────────────────────────────── */
  const isDirty = useMemo(() => {
    if (!userData || !originalData) return false;
    const cA = { ...userData, telefone: unmaskPhone(userData.telefone), cpf: unmaskCPF(userData.cpf) };
    const cB = { ...originalData, telefone: unmaskPhone(originalData.telefone), cpf: unmaskCPF(originalData.cpf) };
    return JSON.stringify(cA) !== JSON.stringify(cB);
  }, [userData, originalData]);

  const handleCloseGuard = () => {
    if (isDirty && !window.confirm('Existem alterações não salvas. Deseja sair mesmo assim?')) return;
    onClose?.();
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    if (name === 'email')    v = normalizeEmail(v);
    if (name === 'telefone') v = maskPhoneBR(v);
    if (name === 'cpf')      v = maskCPF(v);
    if (name === 'mfa_remember_days') {
      const parsed = parseInt(String(v), 10);
      v = Number.isFinite(parsed) && parsed > 0 ? parsed : '';
    }
    setUserData(prev => ({ ...prev, [name]: v }));
  };
  const toggleAuth = (field) => {
    setUserData(prev => {
      const next = { ...prev, [field]: !prev[field] };
      if ((field === 'sso_google' || field === 'sso_microsoft') && next[field]) next.login_local_enabled = false;
      return next;
    });
  };
  const toggleAtivo = () => setUserData(prev => ({ ...prev, ativo: !prev.ativo }));

  /* ─── [APP] Liberar acesso ao app ─────────────────────────────────────────
   * Cascata inversa: ao DESLIGAR o acesso ao app, desliga também
   * "pode_registrar_os" (não faz sentido registrar OS sem acesso ao app).
   * ───────────────────────────────────────────────────────────────────────── */
  const toggleLiberarAcessoApp = () => {
    setUserData(prev => {
      const ligado = !prev.liberar_acesso_app;
      return {
        ...prev,
        liberar_acesso_app: ligado,
        // Se desligou o acesso, derruba o registrar OS junto.
        pode_registrar_os: ligado ? prev.pode_registrar_os : false,
      };
    });
  };

  /* ─── [APP] Pode registrar OS ──────────────────────────────────────────────
   * Cascata direta: ao LIGAR "pode_registrar_os", liga automaticamente
   * "liberar_acesso_app" (pré-requisito para o botão funcionar no app).
   * ───────────────────────────────────────────────────────────────────────── */
  const togglePodeRegistrarOs = () => {
    setUserData(prev => {
      const ligado = !prev.pode_registrar_os;
      return {
        ...prev,
        pode_registrar_os: ligado,
        // Se ligou o registrar OS, garante o acesso ao app.
        liberar_acesso_app: ligado ? true : prev.liberar_acesso_app,
      };
    });
  };

  /* ─── NOVO: IP whitelist helpers ──────────────────────────────────────── */
  const addIp = () => {
    const ip = ipInput.trim();
    if (!ip) return;
    if (!isValidIpOrCidr(ip)) { toast.warning('IP ou CIDR inválido. Ex: 192.168.1.0/24 ou 10.0.0.1'); return; }
    const current = userData.regras_acesso.ips_permitidos || [];
    if (current.includes(ip)) { toast.info('IP já adicionado.'); return; }
    setUserData(prev => ({
      ...prev,
      regras_acesso: { ...prev.regras_acesso, ips_permitidos: [...current, ip] },
    }));
    setIpInput('');
  };
  const removeIp = (idx) => {
    setUserData(prev => ({
      ...prev,
      regras_acesso: {
        ...prev.regras_acesso,
        ips_permitidos: prev.regras_acesso.ips_permitidos.filter((_, i) => i !== idx),
      },
    }));
  };

  /* ─── Validação antes de salvar ───────────────────────────────────────── */
  const validateBeforeSave = () => {
    if (!userData.nome?.trim())         { toast.warning('Informe o nome completo.'); setActiveTab('dados'); return false; }
    if (!isValidEmail(userData.email))  { toast.warning('E-mail inválido.'); setActiveTab('dados'); return false; }
    if (userData.cpf && !validateCPF(userData.cpf)) { toast.warning('CPF inválido.'); setActiveTab('dados'); return false; }
    if (userData.regras_acesso?.dias?.length === 0)  { toast.warning('Selecione pelo menos um dia de logon.'); setActiveTab('regras'); return false; }
    if (!isTimeBefore(userData.regras_acesso?.inicio, userData.regras_acesso?.fim)) {
      toast.warning('Horário inicial deve ser anterior ao final.'); setActiveTab('regras'); return false;
    }
    if (userData.mfa_enabled) {
      const days = parseInt(String(userData.mfa_remember_days), 10);
      if (!Number.isFinite(days) || days <= 0) { toast.warning('Informe um número válido de dias para MFA.'); setActiveTab('auth'); return false; }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) return;
    try {
      setSaving(true);
      await api.put(`/api/usuarios/${userData.id}`, {
        nome: userData.nome.trim(),
        email: normalizeEmail(userData.email || ''),
        telefone: unmaskPhone(userData.telefone || ''),
        perfil: userData.perfil || 'operacional',
        matricula: userData.matricula || null,
        cpf: unmaskCPF(userData.cpf || '') || null,
        filial: userData.filial || null,
        cargo: userData.cargo || null,
        centroDeCusto: userData.centro_custo || null,
        cliente_id: userData.cliente_id === '' ? null : userData.cliente_id,
        forcar_troca_senha_90dias: !!userData.forcar_troca_senha_90dias,
        liberar_acesso_app: !!userData.liberar_acesso_app,
        pode_registrar_os: !!userData.pode_registrar_os,
        ativo: !!userData.ativo,
        sso_google: !!userData.sso_google,
        sso_microsoft: !!userData.sso_microsoft,
        mfa_enabled: !!userData.mfa_enabled,
        login_local_enabled: !!userData.login_local_enabled,
        tour_concluido: userData.tour_concluido ? 1 : 0,
        mfa_remember_days: Number.isFinite(Number(userData.mfa_remember_days)) ? Number(userData.mfa_remember_days) : 7,
      });
      // ── Salva regras (incluindo novos campos) ──────────────────────────
      await api.put(`/api/usuarios/${userData.id}/regras-acesso`, {
        regras: {
          dias:                   userData.regras_acesso?.dias || [],
          inicio:                 userData.regras_acesso?.inicio || null,
          fim:                    userData.regras_acesso?.fim    || null,
          ips_permitidos:         userData.regras_acesso?.ips_permitidos || [],
          timeout_inatividade_min: userData.regras_acesso?.timeout_inatividade_min || null,
        },
      });
      toast.success('Dados e Políticas atualizados com sucesso!');
      onUpdate?.(); onClose?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Falha ao salvar.');
    } finally { setSaving(false); }
  };

  /* ─── Senha ───────────────────────────────────────────────────────────── */
  const handleSalvarSenha = useCallback(async ({ enviarSms = false } = {}) => {
    if (!newPassword) { toast.warning('Digite ou gere uma senha primeiro.'); return; }
    if (!isPasswordValid(newPassword)) { toast.warning('A senha não atende às regras de segurança.'); return; }
    setPwdSaving(true);
    try {
      await api.put(`/api/usuarios/${userData.id}/password`, { newPassword });
      toast.success('Senha salva! O usuário deverá alterá-la no próximo login.');
      const tel = unmaskPhone(userData.telefone);
      if (enviarSms && tel) {
        await api.post(`/api/usuarios/${userData.id}/send-sms-password`, { customPassword: newPassword });
        toast.success(`SMS enviado para …${tel.slice(-4)}.`);
      }
      setNewPassword(''); setShowPwd(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Falha ao salvar a senha.');
    } finally { setPwdSaving(false); }
  }, [newPassword, userData]);

  /* ─── Onboarding ──────────────────────────────────────────────────────── */
  const resetOnboardingWithLogout = async () => {
    if (!window.confirm('Resetar o onboarding deste usuário?\n\n• A sessão ativa será encerrada\n• No próximo login, o tour de boas-vindas será exibido automaticamente')) return;
    try {
      setSaving(true);
      await api.put(`/api/usuarios/${userData.id}`, { tour_concluido: 0 });
      await api.post(`/api/usuarios/logout-force/${userData.id}`);
      setUserData(prev => ({ ...prev, tour_concluido: 0 }));
      toast.success('Onboarding resetado. O colaborador verá o tour no próximo login.');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Falha ao resetar onboarding.');
    } finally { setSaving(false); }
  };

  /* ─── Impersonação ────────────────────────────────────────────────────── */
  const handleImpersonate = async () => {
    if (!window.confirm(`Você está prestes a entrar no sistema COMO "${userData.nome}".\nUma nova aba será aberta com a sessão deste usuário.\nContinuar?`)) return;
    try {
      setImpersonating(true);
      const { data } = await api.post(`/api/usuarios/${userData.id}/impersonate`);
      const url = `${window.location.origin}/dashboard?impersonation_token=${data.token}&impersonated_name=${encodeURIComponent(userData.nome)}`;
      window.open(url, '_blank');
      toast.info(`Sessão aberta como ${userData.nome}. Todas as ações ficam registradas.`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Recurso não disponível.');
    } finally { setImpersonating(false); }
  };

  /* ─── Expiração ───────────────────────────────────────────────────────── */
  const handleSetExpiry = async () => {
    if (!expiryDate) { toast.warning('Selecione uma data de expiração.'); return; }
    if (new Date(expiryDate) < new Date()) { toast.warning('A data deve ser futura.'); return; }
    try {
      setSaving(true);
      await api.put(`/api/usuarios/${userData.id}`, { data_expiracao_conta: expiryDate });
      toast.success(`Conta expirará em ${new Date(expiryDate).toLocaleDateString('pt-BR')}.`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Falha ao definir expiração.');
    } finally { setSaving(false); }
  };
  const handleClearExpiry = async () => {
    try {
      setSaving(true);
      await api.put(`/api/usuarios/${userData.id}`, { data_expiracao_conta: null });
      setExpiryDate(''); toast.success('Expiração de conta removida.');
    } catch { toast.error('Falha ao remover expiração.'); }
    finally  { setSaving(false); }
  };

  /* ─── [TRIAL] Período de teste ─────────────────────────────────────────── */
  const handleSaveTrial = async () => {
    if (trialEnabled && !trialExpiresLocal) {
      toast.warning('Defina a data e hora de término do teste.'); return;
    }
    if (trialEnabled) {
      const iso = spInputToUtcIso(trialExpiresLocal);
      if (!iso || new Date(iso).getTime() <= Date.now()) {
        toast.warning('O término do teste deve ser uma data/hora futura.'); return;
      }
    }
    try {
      setSavingTrial(true);
      await api.put(`/api/usuarios/${userData.id}`, {
        trial_enabled: trialEnabled,
        trial_expires_at: trialEnabled ? spInputToUtcIso(trialExpiresLocal) : null,
      });
      toast.success(trialEnabled
        ? 'Período de teste salvo. O bloqueio ocorrerá automaticamente no horário.'
        : 'Período de teste desativado.');
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Falha ao salvar período de teste.');
    } finally { setSavingTrial(false); }
  };

  /* ─── Logout forçado com mensagem ─────────────────────────────────────── */
  const handleForceLogoutWithMessage = async () => {
    if (!logoutMsg.trim()) { toast.warning('Digite uma mensagem para o usuário.'); return; }
    try {
      setSendingLogout(true);
      await api.put(`/api/usuarios/${userData.id}`, { session_revoke_message: logoutMsg.trim() });
      await api.post(`/api/usuarios/logout-force/${userData.id}`);
      toast.success('Sessão encerrada. O usuário verá a mensagem no próximo acesso.');
      setLogoutMsg('');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Falha ao encerrar sessão.');
    } finally { setSendingLogout(false); }
  };

  /* ─── MFA setup ───────────────────────────────────────────────────────── */
  const startMfaSetup = async () => {
    try {
      setMfaBusy(true); setMfaQrDataUrl(''); setMfaSecret(''); setMfaTotpCode('');
      const { data } = await api.post('/api/usuarios/mfa/setup/start', { email: userData.email });
      setMfaQrDataUrl(data.qrDataUrl); setMfaSecret(data.secret); setMfaModalOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Falha ao iniciar configuração de MFA.');
    } finally { setMfaBusy(false); }
  };
  const verifyMfaSetup = async () => {
    if (!mfaTotpCode?.trim()) { toast.warning('Digite o código do app autenticador.'); return; }
    try {
      setMfaBusy(true);
      await api.post('/api/usuarios/mfa/setup/verify', { email: userData.email, code: mfaTotpCode.trim() });
      toast.success('MFA habilitado com sucesso.');
      setUserData(prev => ({ ...prev, mfa_enabled: true }));
      setMfaModalOpen(false); setMfaTotpCode('');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Código inválido. Tente novamente.');
    } finally { setMfaBusy(false); }
  };
  const resetUserMfa = async () => {
    if (!window.confirm('Resetar o MFA deste usuário? Ele precisará reconfigurar o app autenticador no próximo login.')) return;
    try {
      setSaving(true);
      await api.post(`/api/usuarios/${userData.id}/mfa/reset`);
      toast.success('MFA resetado com sucesso.');
      setUserData(prev => ({ ...prev, mfa_enabled: true }));
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Falha ao resetar MFA.');
    } finally { setSaving(false); }
  };

  /* ─── Helpers locais ──────────────────────────────────────────────────── */
  const pwdRules  = getPasswordRules(newPassword);
  const pwdValid  = pwdRules.every(r => r.ok);
  const hasPhone  = !!unmaskPhone(userData?.telefone || '');
  const pwdBusy   = pwdSaving || saving;

  if (loading || !userData) {
    return (
      <div className="blb-overlay">
        <div className="blb-loader">Carregando Governança...</div>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /* ── RENDER PRINCIPAL ──────────────────────────────────────────────────── */
  /* ──────────────────────────────────────────────────────────────────────── */
  return (
    <div className="blb-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target.classList.contains('blb-overlay')) handleCloseGuard(); }}>
      <div className="blb-modal advanced fade-in" ref={modalRef} aria-labelledby="admin-modal-title">

        {/* SIDEBAR */}
        <aside className="blb-sidebar">
          <div className="user-profile-summary">
            <div className={`avatar-circle ${userData.ativo ? 'active' : 'inactive'}`}>
              {userData.nome ? userData.nome.charAt(0) : 'U'}
            </div>
            <h4 ref={firstFocusableRef}>{(userData.nome || 'Usuário').split(' ')[0]}</h4>
            <span className="user-role">{userData.perfil?.toUpperCase() || 'SEM PERFIL'}</span>
          </div>
          <nav className="nav-menu" aria-label="Navegação de seções">
            <button className={activeTab === 'dados'        ? 'active' : ''} onClick={() => setActiveTab('dados')}><FaIdCard /> Propriedades</button>
            <button className={activeTab === 'regras'       ? 'active' : ''} onClick={() => setActiveTab('regras')}><FaClock /> Regras de Acesso</button>
            <button className={activeTab === 'auth'         ? 'active' : ''} onClick={() => setActiveTab('auth')}><FaKey /> Autenticação SSO</button>
            <button className={activeTab === 'seguranca'    ? 'active' : ''} onClick={() => setActiveTab('seguranca')}><FaUserShield /> Segurança</button>
            <button className={activeTab === 'inteligencia' ? 'active' : ''} onClick={() => setActiveTab('inteligencia')}><FaBrain /> Inteligência</button>
          </nav>
          <button onClick={handleCloseGuard} className="btn-sidebar-exit">Sair do Painel</button>
        </aside>

        {/* CONTEÚDO */}
        <main className="blb-content">
          <header className="content-header">
            <div>
              <h2 id="admin-modal-title">Gestão de Identidade</h2>
              <p>Objeto: {userData.email}</p>
            </div>
            <div className={`status-badge ${userData.ativo ? 'active' : 'inactive'}`} onClick={toggleAtivo} title="Alternar status" role="button" aria-pressed={userData.ativo}>
              <FaCheck /> {userData.ativo ? 'ATIVO' : 'BLOQUEADO'}
            </div>
          </header>

          <div className="content-scrollable">

            {/* ══════════════════════════════════════════ ABA: DADOS */}
            {activeTab === 'dados' && (
              <div className="tab-fade">
                <h3 className="section-label">Dados Básicos e Acesso</h3>
                <div className="blb-grid-2">
                  <div className="blb-form-group"><label>Nome Completo</label><input type="text" name="nome" value={userData.nome || ''} onChange={handleChange} /></div>
                  <div className="blb-form-group"><label>E-mail (Login)</label><input type="email" name="email" value={userData.email || ''} onChange={handleChange} /></div>
                  <div className="blb-form-group"><label>Telefone / WhatsApp</label><input type="text" name="telefone" value={userData.telefone || ''} onChange={handleChange} inputMode="numeric" placeholder="(11) 91234-5678" /></div>
                  <div className="blb-form-group">
                    <label>Perfil de Acesso</label>
                    <select name="perfil" value={userData.perfil || 'operacional'} onChange={handleChange}>
                      <option value="operacional">Operacional / Pátio</option>
                      <option value="financeiro">Financeiro</option>
                      <option value="rh">RH</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                </div>
                <h3 className="section-label" style={{ marginTop: 25 }}>Informações Corporativas</h3>
                <div className="blb-form-group">
                  <label>Empresa de Destino (Cliente)</label>
                  <select name="cliente_id" value={userData.cliente_id || ''} onChange={handleChange}>
                    <option value="">-- Selecione --</option>
                    {allClients.map(c => <option key={c.id} value={c.id}>{c.nome_empresa}</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 15 }}>
                  <div className="blb-form-group"><label>Matrícula</label><input type="text" name="matricula" value={userData.matricula || ''} onChange={handleChange} /></div>
                  <div className="blb-form-group"><label>CPF</label><input type="text" name="cpf" value={userData.cpf || ''} onChange={handleChange} placeholder="000.000.000-00" inputMode="numeric" /></div>
                  <div className="blb-form-group"><label>Filial / Unidade</label><input type="text" name="filial" value={userData.filial || ''} onChange={handleChange} /></div>
                </div>
                <div className="blb-grid-2">
                  <div className="blb-form-group"><label>Cargo</label><input type="text" name="cargo" value={userData.cargo || ''} onChange={handleChange} /></div>
                  <div className="blb-form-group"><label>Centro de Custo</label><input type="text" name="centro_custo" value={userData.centro_custo || ''} onChange={handleChange} /></div>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════ ABA: REGRAS */}
            {activeTab === 'regras' && (
              <div className="tab-fade">

                {/* ── Dias permitidos ─────────────────────────────────────── */}
                <h3 className="section-label">Dias Permitidos de Logon</h3>
                <div className="days-selector">
                  {DAYS.map((dia, idx) => {
                    const checked = userData.regras_acesso.dias.includes(idx);
                    return (
                      <label key={dia} className={`day-checkbox ${checked ? 'checked' : ''}`}>
                        <input type="checkbox" checked={checked} onChange={() => {
                          const dias    = userData.regras_acesso.dias || [];
                          const updated = checked ? dias.filter(d => d !== idx) : sortUnique([...dias, idx]);
                          setUserData(prev => ({ ...prev, regras_acesso: { ...prev.regras_acesso, dias: updated } }));
                        }} />
                        {dia}
                      </label>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button type="button" style={{ ...S.btn('default'), fontSize: 11 }}
                    onClick={() => setUserData(prev => ({ ...prev, regras_acesso: { ...prev.regras_acesso, dias: [0,1,2,3,4,5,6] } }))}>
                    Todos os dias
                  </button>
                  <button type="button" style={{ ...S.btn('default'), fontSize: 11 }}
                    onClick={() => setUserData(prev => ({ ...prev, regras_acesso: { ...prev.regras_acesso, dias: [1,2,3,4,5] } }))}>
                    Seg–Sex
                  </button>
                  <button type="button" style={{ ...S.btn('default'), fontSize: 11 }}
                    onClick={() => setUserData(prev => ({ ...prev, regras_acesso: { ...prev.regras_acesso, dias: [] } }))}>
                    Nenhum
                  </button>
                </div>

                {/* ── Janela de horário ────────────────────────────────────── */}
                <h3 className="section-label" style={{ marginTop: 28 }}>Janela de Horário</h3>
                <div className="blb-grid-2">
                  <div className="blb-form-group">
                    <label>Início Permitido</label>
                    <input type="time" value={userData.regras_acesso.inicio}
                      onChange={e => setUserData(prev => ({ ...prev, regras_acesso: { ...prev.regras_acesso, inicio: e.target.value } }))} />
                  </div>
                  <div className="blb-form-group">
                    <label>Término Permitido</label>
                    <input type="time" value={userData.regras_acesso.fim}
                      onChange={e => setUserData(prev => ({ ...prev, regras_acesso: { ...prev.regras_acesso, fim: e.target.value } }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" style={{ ...S.btn('default'), fontSize: 11 }}
                    onClick={() => setUserData(prev => ({ ...prev, regras_acesso: { ...prev.regras_acesso, inicio: '00:00', fim: '23:59' } }))}>
                    Sem restrição de horário
                  </button>
                  <button type="button" style={{ ...S.btn('default'), fontSize: 11 }}
                    onClick={() => setUserData(prev => ({ ...prev, regras_acesso: { ...prev.regras_acesso, inicio: '08:00', fim: '18:00' } }))}>
                    Comercial (08h–18h)
                  </button>
                  <button type="button" style={{ ...S.btn('default'), fontSize: 11 }}
                    onClick={() => setUserData(prev => ({ ...prev, regras_acesso: { ...prev.regras_acesso, inicio: '07:00', fim: '22:00' } }))}>
                    Estendido (07h–22h)
                  </button>
                </div>

                {/* ══ NOVO 1: Restrição por IP/Rede ══════════════════════════ */}
                <div style={S.divider}><div style={S.dividerLine} /><FaNetworkWired /> RESTRIÇÃO DE ORIGEM (IP)<div style={S.dividerLine} /></div>

                <div style={{ ...S.card('#3b82f6'), marginBottom: 8 }}>
                  <div style={S.cardLabel}><FaNetworkWired /> IP / Rede Permitida</div>
                  <p style={{ fontSize: 12, color: '#475569', marginBottom: 14, lineHeight: 1.6 }}>
                    Quando configurado, o login deste usuário será aceito <strong>somente</strong> a partir
                    destes endereços IP ou faixas CIDR. Ideal para restringir acesso à rede interna ou VPN.
                    Deixe em branco para não restringir.
                  </p>

                  {/* Campo de entrada */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <input
                      type="text"
                      value={ipInput}
                      onChange={e => setIpInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addIp(); } }}
                      placeholder="Ex: 192.168.1.0/24 ou 10.0.0.1"
                      style={{ ...S.input, flex: 1 }}
                    />
                    <button type="button" style={S.btn('primary')} onClick={addIp}>
                      <FaPlus /> Adicionar
                    </button>
                  </div>

                  {/* Tags de IPs */}
                  {userData.regras_acesso.ips_permitidos.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                      {userData.regras_acesso.ips_permitidos.map((ip, idx) => (
                        <span key={idx} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: '#eff6ff', border: '1px solid #bfdbfe',
                          color: '#1d4ed8', borderRadius: 8,
                          fontSize: 12, fontWeight: 700,
                          padding: '5px 10px',
                        }}>
                          <FaNetworkWired size={10} />
                          {ip}
                          <button
                            type="button"
                            onClick={() => removeIp(idx)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: '#93c5fd', padding: 0, lineHeight: 1,
                              display: 'flex', alignItems: 'center',
                            }}
                            title="Remover IP"
                          >
                            <FaTimes size={10} />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      fontSize: 12, color: '#94a3b8',
                      background: '#f8fafc', borderRadius: 8,
                      padding: '10px 14px', fontStyle: 'italic',
                    }}>
                      Nenhum IP configurado — acesso liberado de qualquer origem.
                    </div>
                  )}

                  {userData.regras_acesso.ips_permitidos.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#2563eb', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <FaInfoCircle size={10} />
                      {userData.regras_acesso.ips_permitidos.length} origem(ns) configurada(s). Logins de outros IPs serão bloqueados.
                    </div>
                  )}

                  {/* Atalho: adicionar IP atual */}
                  <button type="button" style={{ ...S.btn('cyan'), marginTop: 12, fontSize: 11 }}
                    onClick={async () => {
                      try {
                        const r = await fetch('https://api.ipify.org?format=json');
                        const d = await r.json();
                        setIpInput(d.ip);
                        toast.info(`IP atual: ${d.ip} — clique em Adicionar para confirmar.`);
                      } catch { toast.warning('Não foi possível detectar o IP atual.'); }
                    }}>
                    <FaMapMarkerAlt size={11} /> Detectar meu IP atual
                  </button>
                </div>

                {/* ══ NOVO 2: Timeout de Inatividade ═════════════════════════ */}
                <div style={S.divider}><div style={S.dividerLine} /><FaHourglassHalf /> TIMEOUT DE INATIVIDADE<div style={S.dividerLine} /></div>

                <div style={{ ...S.card('#8b5cf6'), marginBottom: 8 }}>
                  <div style={S.cardLabel}><FaHourglassHalf /> Auto-logout por Inatividade</div>
                  <p style={{ fontSize: 12, color: '#475569', marginBottom: 14, lineHeight: 1.6 }}>
                    Define o tempo máximo que este usuário pode ficar <strong>inativo</strong> antes de
                    ser desconectado automaticamente. Recomendado para funções com acesso a dados sensíveis.
                  </p>

                  <select
                    value={userData.regras_acesso.timeout_inatividade_min || ''}
                    onChange={e => setUserData(prev => ({
                      ...prev,
                      regras_acesso: {
                        ...prev.regras_acesso,
                        timeout_inatividade_min: e.target.value ? Number(e.target.value) : null,
                      },
                    }))}
                    style={{ ...S.input, width: '100%', marginBottom: 10 }}
                  >
                    {TIMEOUT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                  {userData.regras_acesso.timeout_inatividade_min && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: '#f5f3ff', border: '1px solid #ddd6fe',
                      borderRadius: 8, padding: '8px 12px',
                      fontSize: 12, color: '#7c3aed', fontWeight: 600,
                    }}>
                      <FaHourglassHalf size={12} />
                      Sessão expira após {userData.regras_acesso.timeout_inatividade_min} minuto(s) sem atividade.
                    </div>
                  )}

                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10, lineHeight: 1.5 }}>
                    💡 O frontend deve consumir este campo via <code style={{ fontSize: 11 }}>regras_acesso.timeout_inatividade_min</code> na resposta do <code style={{ fontSize: 11 }}>/api/auth/me</code> para implementar o contador de inatividade.
                  </div>
                </div>

                {/* Resumo da política ativa */}
                <div style={{ ...S.card('#0f172a'), marginTop: 16 }}>
                  <div style={{ ...S.cardLabel, color: '#94a3b8' }}><FaShieldAlt /> Resumo da Política de Acesso</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <PolicyLine label="Dias" value={
                      userData.regras_acesso.dias.length === 7 ? 'Todos os dias'
                      : userData.regras_acesso.dias.length === 0 ? '⛔ Nenhum — acesso bloqueado'
                      : userData.regras_acesso.dias.map(d => DAYS[d]).join(', ')
                    } />
                    <PolicyLine label="Horário"  value={`${userData.regras_acesso.inicio || '00:00'} → ${userData.regras_acesso.fim || '23:59'}`} />
                    <PolicyLine label="IPs"      value={
                      userData.regras_acesso.ips_permitidos.length
                        ? `${userData.regras_acesso.ips_permitidos.length} origem(ns) restrita(s)`
                        : 'Qualquer origem'
                    } />
                    <PolicyLine label="Inatividade" value={
                      userData.regras_acesso.timeout_inatividade_min
                        ? `Auto-logout após ${userData.regras_acesso.timeout_inatividade_min} min`
                        : 'Sem timeout'
                    } />
                  </div>
                </div>

              </div>
            )}

            {/* ══════════════════════════════════════════ ABA: AUTH */}
            {activeTab === 'auth' && (
              <div className="tab-fade">
                <h3 className="section-label">Provedores de Identidade</h3>
                <div className="cards-grid">
                  <div className={`auth-card google ${userData.sso_google ? 'active' : 'inactive'}`} onClick={() => toggleAuth('sso_google')}>
                    <FaGoogle size={24} />
                    <div className="card-text"><strong>Google Workspace</strong><span>{userData.sso_google ? 'Habilitado' : 'Desabilitado'}</span></div>
                  </div>
                  <div className={`auth-card microsoft ${userData.sso_microsoft ? 'active' : 'inactive'}`} onClick={() => toggleAuth('sso_microsoft')}>
                    <FaMicrosoft size={24} />
                    <div className="card-text"><strong>Microsoft Entra ID</strong><span>{userData.sso_microsoft ? 'Habilitado' : 'Desabilitado'}</span></div>
                  </div>
                </div>
                <h3 className="section-label" style={{ marginTop: 30 }}>Segurança Reforçada</h3>
                <div className={`auth-card mfa ${userData.mfa_enabled ? 'active' : 'inactive'}`} onClick={() => toggleAuth('mfa_enabled')}>
                  <div className="icon-box"><FaLock /></div>
                  <div className="card-text"><strong>MFA - Autenticação Multifator (TOTP)</strong><span>{userData.mfa_enabled ? 'Habilitado' : 'Desabilitado'}</span></div>
                  <div className="toggle-visual"><div className="toggle-knob" /></div>
                </div>
                <h3 className="section-label" style={{ marginTop: 30 }}>Login Local (Senha)</h3>
                <div className={`auth-card local-login ${userData.login_local_enabled ? 'active' : 'inactive'}`} onClick={() => setUserData(prev => ({ ...prev, login_local_enabled: !prev.login_local_enabled }))}>
                  <div className="icon-box"><FaLock /></div>
                  <div className="card-text"><strong>Login por senha (local)</strong><span>{userData.login_local_enabled ? 'Permitido' : 'Bloqueado'}</span></div>
                  <div className="toggle-visual"><div className="toggle-knob" /></div>
                </div>
                {(userData.sso_google || userData.sso_microsoft) && !userData.login_local_enabled && (
                  <p style={{ marginTop: 10, opacity: .8 }}>✅ Política ativa: SSO habilitado — login local bloqueado automaticamente.</p>
                )}
                <h3 className="section-label" style={{ marginTop: 30 }}>Acesso ao Aplicativo Mobile</h3>
                <div className={`auth-card ${userData.liberar_acesso_app ? 'active' : 'inactive'}`} onClick={toggleLiberarAcessoApp}>
                  <div className="icon-box"><FaMobileAlt /></div>
                  <div className="card-text">
                    <strong>Liberar acesso ao app</strong>
                    <span>{userData.liberar_acesso_app ? 'Pode entrar no app do motorista' : 'Sem acesso ao app'}</span>
                  </div>
                  <div className="toggle-visual"><div className="toggle-knob" /></div>
                </div>
                <p style={{ marginTop: 10, opacity: .8, fontSize: 12, color: '#475569' }}>
                  ℹ️ Permite que este usuário entre no app mesmo sem perfil operacional. Ao entrar, ele escolhe atuar como <strong>Motorista</strong> ou <strong>ADM</strong>.
                </p>

                {/* ══ NOVO: Registrar OS pelo app ══════════════════════════════
                    Logo abaixo de "Liberar acesso ao app".
                    Ligar este toggle marca automaticamente o acesso ao app.
                    Desligar o acesso ao app derruba este toggle (cascata inversa,
                    tratada em toggleLiberarAcessoApp). ───────────────────────── */}
                <div
                  className={`auth-card ${userData.pode_registrar_os ? 'active' : 'inactive'}`}
                  onClick={togglePodeRegistrarOs}
                  style={{ marginTop: 12 }}
                >
                  <div className="icon-box"><FaClipboardCheck /></div>
                  <div className="card-text">
                    <strong>Registrar OS pelo app</strong>
                    <span>
                      {userData.pode_registrar_os
                        ? 'Pode registrar OS (Serviço/Tarefa) para si ou outros motoristas'
                        : 'Sem permissão para registrar OS pelo app'}
                    </span>
                  </div>
                  <div className="toggle-visual"><div className="toggle-knob" /></div>
                </div>
                <p style={{ marginTop: 10, opacity: .8, fontSize: 12, color: '#475569' }}>
                  ℹ️ Libera o botão de registrar OS dentro do app. Ao ativar, o <strong>acesso ao app</strong> é
                  habilitado automaticamente (pré-requisito). A mudança chega ao aparelho do usuário em tempo real.
                </p>

                <h3 className="section-label" style={{ marginTop: 30 }}>Política de MFA</h3>
                <div className="blb-grid-2">
                  <div className="blb-form-group">
                    <label>Lembrar dispositivo (dias)</label>
                    <input type="number" name="mfa_remember_days" min={1} value={userData.mfa_remember_days ?? 7} onChange={handleChange} />
                    <small>Período sem novo código MFA no mesmo dispositivo (cookie HttpOnly).</small>
                  </div>
                  <div className="blb-form-group">
                    <label>App Autenticador (TOTP)</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" className="btn-primary" onClick={startMfaSetup} disabled={mfaBusy}>
                        {mfaBusy ? 'Gerando QR…' : (userData.mfa_enabled ? 'Refazer Configuração' : 'Configurar App')}
                      </button>
                      <button type="button" className="btn-danger" onClick={resetUserMfa} disabled={saving}>
                        <FaRedoAlt style={{ marginRight: 6 }} /> Resetar MFA
                      </button>
                    </div>
                    <small>Use se o colaborador perdeu/trocou o celular.</small>
                  </div>
                </div>

                {mfaModalOpen && (
                  <div className="blb-overlay" role="dialog" aria-modal="true" onMouseDown={e => { if (e.target.classList.contains('blb-overlay')) setMfaModalOpen(false); }}>
                    <div className="blb-modal simple fade-in" aria-labelledby="mfa-setup-title">
                      <div className="blb-modal-header">
                        <div className="header-title">
                          <div className="icon-badge"><FaLock /></div>
                          <div><h3 id="mfa-setup-title">Configurar App Autenticador (TOTP)</h3><span>Escaneie o QR e valide o código</span></div>
                        </div>
                        <button onClick={() => setMfaModalOpen(false)} className="btn-close"><FaTimes /></button>
                      </div>
                      <div className="blb-modal-body">
                        <div className="blb-grid-2" style={{ alignItems: 'center' }}>
                          <div style={{ textAlign: 'center' }}>
                            {mfaQrDataUrl ? <img src={mfaQrDataUrl} alt="QR TOTP" style={{ maxWidth: 220 }} /> : <div className="blb-loader">Gerando QR...</div>}
                          </div>
                          <div>
                            <div className="blb-form-group"><label>Secret (opcional)</label><input type="text" readOnly value={mfaSecret || ''} /></div>
                            <div className="blb-form-group">
                              <label>Código do App (6 dígitos)</label>
                              <input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={mfaTotpCode} onChange={e => setMfaTotpCode(e.target.value)} />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="blb-modal-footer">
                        <button onClick={() => setMfaModalOpen(false)} className="btn-ghost">Cancelar</button>
                        <button onClick={verifyMfaSetup} className="btn-primary" disabled={mfaBusy || !mfaTotpCode}>{mfaBusy ? 'Verificando…' : 'Validar e Habilitar MFA'}</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══════════════════════════════════════════ ABA: SEGURANÇA */}
            {activeTab === 'seguranca' && (
              <div className="tab-fade" style={{ background: '#f8fafc', borderRadius: 12, padding: '24px', margin: '-10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '12px 16px', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                  <FaShieldAlt style={{ color: '#3b82f6', fontSize: 18 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', letterSpacing: -.3 }}>Identity Security Panel</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', marginTop: 2 }}>dn: uid={userData.email}, ou=users, dc=blbsystem, dc=com</div>
                  </div>
                </div>
                <div style={S.grid2}>
                  <div style={S.card('#3b82f6')}>
                    <div style={S.cardLabel}><FaIdCard /> Account Object</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <AttrRow label="accountStatus"     value={<span style={S.pill(userData.ativo ? '#16a34a' : '#dc2626', userData.ativo ? '#f0fdf4' : '#fff1f0')}>{userData.ativo ? <FaCheckCircle /> : <FaBan />} {userData.ativo ? 'ENABLED' : 'DISABLED'}</span>} />
                      <AttrRow label="userPrincipalName" value={<span style={{ fontSize: 11, color: '#2563eb', fontFamily: 'monospace' }}>{userData.email}</span>} />
                      <AttrRow label="memberOf"          value={<span style={S.pill('#7c3aed','#f5f3ff')}>{userData.perfil?.toUpperCase()}</span>} />
                      <AttrRow label="mfaEnabled"        value={<span style={S.pill(userData.mfa_enabled?'#16a34a':'#c2410c', userData.mfa_enabled?'#f0fdf4':'#fff7ed')}>{userData.mfa_enabled?'TRUE':'FALSE'}</span>} />
                      {expiryDate && <AttrRow label="accountExpires" value={<span style={S.pill('#c2410c','#fff7ed')}><FaCalendarAlt size={9} /> {new Date(expiryDate).toLocaleDateString('pt-BR')}</span>} />}
                    </div>
                  </div>
                  <div style={S.card('#10b981')}>
                    <div style={S.cardLabel}><FaDesktop /> Session Info</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <AttrRow label="lastLogon"         value={<span style={{ fontSize: 11, color: '#059669', fontFamily: 'monospace' }}>{userData.ultimo_acesso ? fmtDate(userData.ultimo_acesso) : 'Nunca'}</span>} />
                      <AttrRow label="localLoginEnabled" value={<span style={S.pill(userData.login_local_enabled?'#16a34a':'#dc2626', userData.login_local_enabled?'#f0fdf4':'#fff1f0')}>{userData.login_local_enabled?'TRUE':'FALSE'}</span>} />
                      <AttrRow label="ssoGoogle"         value={<span style={S.pill(userData.sso_google?'#16a34a':'#94a3b8', userData.sso_google?'#f0fdf4':'#f8fafc')}>{userData.sso_google?'TRUE':'FALSE'}</span>} />
                      <AttrRow label="ssoMicrosoft"      value={<span style={S.pill(userData.sso_microsoft?'#16a34a':'#94a3b8', userData.sso_microsoft?'#f0fdf4':'#f8fafc')}>{userData.sso_microsoft?'TRUE':'FALSE'}</span>} />
                    </div>
                  </div>
                </div>

                <div style={S.divider}><div style={S.dividerLine} /> ONBOARDING <div style={S.dividerLine} /></div>
                <div style={{ ...S.card('#7c3aed'), marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <FaRocket style={{ color: '#7c3aed', fontSize: 16 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>welcomeTour</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{userData.tour_concluido ? 'Status: CONCLUIDO' : 'Status: PENDENTE — tour exibido no próximo logon'}</div>
                      </div>
                    </div>
                    <span style={S.pill(userData.tour_concluido?'#64748b':'#7c3aed', userData.tour_concluido?'#f8fafc':'#f5f3ff')}>{userData.tour_concluido?'COMPLETED':'ACTIVE'}</span>
                  </div>
                  <div style={{ ...S.actionRow, marginTop: 12 }}>
                    <button type="button" style={S.btn('purple')} onClick={resetOnboardingWithLogout} disabled={saving}><FaRedoAlt /> Reset Tour + Forçar Re-login</button>
                    <button type="button" style={S.btn('default')} onClick={() => setUserData(prev => ({ ...prev, tour_concluido: prev.tour_concluido ? 0 : 1 }))}>{userData.tour_concluido ? 'Marcar como Pendente' : 'Marcar como Concluído'}</button>
                  </div>
                </div>

                <div style={S.divider}><div style={S.dividerLine} /> CREDENTIAL ACTIONS <div style={S.dividerLine} /></div>
                <div style={{ ...S.card('#6366f1'), marginBottom: 14 }}>
                  <div style={S.cardLabel}><FaLock /> Definir Nova Senha Provisória</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input type={showPwd?'text':'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        placeholder="Digite ou gere uma senha forte"
                        style={{ ...S.input, width:'100%', boxSizing:'border-box', paddingRight:40, fontFamily:showPwd?'monospace':'inherit', border: newPassword?(pwdValid?'1.5px solid #16a34a':'1.5px solid #f59e0b'):'1px solid #e2e8f0' }} />
                      <button type="button" onClick={() => setShowPwd(p=>!p)} style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#64748b',padding:4,display:'flex' }}>
                        {showPwd ? <FaEyeSlash size={14}/> : <FaEye size={14}/>}
                      </button>
                    </div>
                    <button type="button" style={S.btn('purple')} onClick={() => { setNewPassword(generateStrongPassword()); setShowPwd(true); }}><FaDice size={13}/> Gerar</button>
                    {newPassword && <button type="button" style={S.btn('default')} onClick={() => { navigator.clipboard?.writeText(newPassword); toast.info('Copiado!'); }}><FaCopy size={13}/></button>}
                  </div>
                  {newPassword.length > 0 && (
                    <>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
                        {pwdRules.map(rule => (
                          <span key={rule.key} style={{ display:'inline-flex',alignItems:'center',gap:5,fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:99,color:rule.ok?'#16a34a':'#b45309',background:rule.ok?'#f0fdf4':'#fffbeb',border:`1px solid ${rule.ok?'#bbf7d0':'#fde68a'}` }}>
                            {rule.ok?<FaCheck size={9}/>:<FaTimes size={9}/>}{rule.label}
                          </span>
                        ))}
                      </div>
                      <div style={{ marginBottom:14 }}>
                        <div style={{ height:4,borderRadius:4,background:'#e2e8f0',overflow:'hidden' }}>
                          <div style={{ height:'100%',width:`${(pwdRules.filter(r=>r.ok).length/pwdRules.length)*100}%`,background:pwdValid?'#16a34a':pwdRules.filter(r=>r.ok).length>=3?'#f59e0b':'#ef4444',borderRadius:4,transition:'width .3s,background .3s' }} />
                        </div>
                      </div>
                    </>
                  )}
                  <div style={{ ...S.actionRow, marginTop: 4 }}>
                    <button type="button" style={{ ...S.btn(pwdValid?'success':'default'), opacity:(!newPassword||!pwdValid||pwdBusy)?0.55:1 }}
                      onClick={() => handleSalvarSenha({ enviarSms: hasPhone })} disabled={!newPassword||!pwdValid||pwdBusy}>
                      <FaCheck />{pwdBusy?'Salvando…':hasPhone?'Salvar + Enviar SMS':'Salvar Senha'}
                    </button>
                    {hasPhone && <button type="button" style={S.btn('warn')} onClick={() => handleSalvarSenha({ enviarSms: true })} disabled={!newPassword||!pwdValid||pwdBusy}><FaSms /> SMS</button>}
                  </div>
                </div>

                <div style={S.divider}><div style={S.dividerLine} /> ENTERPRISE FEATURES <div style={S.dividerLine} /></div>
                <div style={S.grid2}>
                  <div style={S.card('#10b981')}>
                    <div style={S.cardLabel}><FaUserSecret /> Impersonation</div>
                    <div style={{ fontSize:12,color:'#475569',marginBottom:12,lineHeight:1.5 }}>Abre uma nova aba logado como este usuário para debug. Todas as ações são registradas.</div>
                    <button type="button" style={S.btn('success')} onClick={handleImpersonate} disabled={impersonating}><FaUserSecret /> {impersonating?'Abrindo…':`Entrar como ${userData.nome.split(' ')[0]}`}</button>
                  </div>
                  <div style={S.card('#f59e0b')}>
                    <div style={S.cardLabel}><FaCalendarAlt /> Account Expiry</div>
                    <div style={{ fontSize:12,color:'#475569',marginBottom:12,lineHeight:1.5 }}>Define a data em que a conta será bloqueada automaticamente.</div>
                    <div style={{ display:'flex',gap:8,alignItems:'center' }}>
                      <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} min={new Date().toISOString().slice(0,10)} style={{ ...S.input,flex:1 }} />
                      <button type="button" style={S.btn('default')} onClick={handleSetExpiry} disabled={saving||!expiryDate}><FaCheck /></button>
                      {expiryDate && <button type="button" style={S.btn('danger')} onClick={handleClearExpiry} disabled={saving}><FaTrash /></button>}
                    </div>
                  </div>
                </div>
                <div style={{ ...S.card('#f97316'),marginBottom:12 }}>
                  <div style={S.cardLabel}><FaCommentAlt /> Force Logoff with Message</div>
                  <textarea value={logoutMsg} onChange={e => setLogoutMsg(e.target.value)} placeholder="Ex: Sua conta foi temporariamente suspensa para verificação de segurança." style={{ ...S.textarea,width:'100%',boxSizing:'border-box',marginBottom:10 }} maxLength={300} />
                  <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                    <span style={{ fontSize:11,color:'#475569' }}>{logoutMsg.length}/300</span>
                    <button type="button" style={S.btn('warn')} onClick={handleForceLogoutWithMessage} disabled={sendingLogout||!logoutMsg.trim()}><FaSignOutAlt /> {sendingLogout?'Encerrando…':'Encerrar Sessão + Notificar'}</button>
                  </div>
                </div>
                {/* ══ [TRIAL] Período de Teste ═══════════════════════════════ */}
                <div style={S.divider}><div style={S.dividerLine} /><FaHourglassHalf /> PERÍODO DE TESTE (TRIAL)<div style={S.dividerLine} /></div>
                {clienteTrialEnabled ? (
                  /* Trial controlado pelo CLIENTE → unitário bloqueado */
                  <div style={{ ...S.card('#f59e0b'), marginBottom: 12 }}>
                    <div style={S.cardLabel}><FaHourglassHalf /> Trial controlado pelo cliente</div>
                    <div style={{ fontSize: 12, color: '#475569', marginBottom: 12, lineHeight: 1.5 }}>
                      Este tenant está com o <strong>período de teste definido no nível do cliente</strong>,
                      que prevalece sobre o trial individual. Para alterar, use a aba
                      <strong> Ações Críticas</strong> nas configurações do cliente (System Hub).
                    </div>
                    <TrialCountdown expiresAt={clienteTrialExpiresAt} label="Trial do cliente (herdado)" />
                  </div>
                ) : (
                  <div style={{ ...S.card('#f97316'), marginBottom: 12 }}>
                    <div style={S.cardLabel}><FaHourglassHalf /> Trial de Acesso</div>
                    <div style={{ fontSize: 12, color: '#475569', marginBottom: 12, lineHeight: 1.5 }}>
                      Quando ativo, o acesso é <strong>bloqueado automaticamente</strong> no horário definido
                      (horário de Brasília). O usuário é deslogado e, ao tentar entrar, vê a mensagem
                      “Seu período de testes acabou. Acione o gestor da conta.”.
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
                      <input type="checkbox" checked={trialEnabled} onChange={e => setTrialEnabled(e.target.checked)} />
                      Ativar período de teste para este usuário
                    </label>

                    {trialEnabled && (
                      <>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
                          <input
                            type="datetime-local"
                            value={trialExpiresLocal}
                            onChange={e => setTrialExpiresLocal(e.target.value)}
                            min={utcToSpInput(new Date().toISOString())}
                            style={{ ...S.input, flex: 1 }}
                          />
                        </div>
                        <div style={{ marginBottom: 14 }}>
                          <TrialCountdown expiresAt={spInputToUtcIso(trialExpiresLocal)} label="Prévia do contador" />
                        </div>
                      </>
                    )}

                    <button type="button" style={S.btn(trialEnabled ? 'warn' : 'default')}
                      onClick={handleSaveTrial} disabled={savingTrial}>
                      <FaCheck /> {savingTrial ? 'Salvando…' : 'Salvar Período de Teste'}
                    </button>
                  </div>
                )}

                <div style={S.divider}><div style={S.dividerLine} /> DANGER ZONE <div style={S.dividerLine} /></div>
                <div style={S.dangerZone}>
                  <div style={{ fontSize:11,color:'#f87171',fontWeight:700,marginBottom:10,display:'flex',alignItems:'center',gap:6 }}><FaExclamationTriangle /> Ações irreversíveis — requerem confirmação</div>
                  <div style={S.actionRow}>
                    <button type="button" style={S.btn('danger')} onClick={toggleAtivo}>{userData.ativo?<><FaBan /> Desabilitar Conta</>:<><FaCheckCircle /> Reabilitar Conta</>}</button>
                    <button type="button" style={S.btn('danger')} onClick={() => {
                      if (window.confirm('ATENÇÃO: Isto irá bloquear o usuário E encerrar todas as sessões. Confirmar?')) {
                        api.put(`/api/usuarios/${userData.id}/status`, { status: 'bloqueado' })
                          .then(() => api.post(`/api/usuarios/logout-force/${userData.id}`))
                          .then(() => { toast.success('Conta bloqueada e sessões encerradas.'); onUpdate?.(); })
                          .catch(() => toast.error('Falha ao bloquear conta.'));
                      }
                    }}><FaSignOutAlt /> Bloquear + Derrubar Sessão</button>
                  </div>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════ ABA: INTELIGÊNCIA */}
            {activeTab === 'inteligencia' && (
              <div className="tab-fade">
                {auditLoading ? (
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:60, gap:16 }}>
                    <div style={{ width:48,height:48,borderRadius:24,background:'linear-gradient(135deg,#3b82f6,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                      <FaBrain size={22} color="#fff" />
                    </div>
                    <div style={{ fontSize:14,fontWeight:700,color:'#0f172a' }}>Carregando dados de inteligência…</div>
                    <div style={{ fontSize:12,color:'#94a3b8' }}>Analisando auditoria e padrões de acesso</div>
                  </div>
                ) : (() => {
                  const logs     = auditData?.logs || [];
                  const posthog  = auditData?.posthog;
                  const risk     = calcRiskScore(userData, logs);
                  const { matrix: heatMatrix, maxVal: heatMax } = buildHeatmap(logs);

                  const thisWeek = logs.filter(l => {
                    const d = new Date((l.created_at||'').toString().replace(' ','T'));
                    return (Date.now() - d) < 7 * 86400000;
                  }).length;

                  return (
                    <>
                      {/* ── Summary cards ──────────────────────────────────── */}
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:22 }}>
                        <SummaryCard icon={<FaHistory size={18} color="#3b82f6"/>} bg="#eff6ff" label="Total de eventos" value={logs.length} unit="registros" />
                        <SummaryCard icon={<FaChartBar size={18} color="#8b5cf6"/>} bg="#f5f3ff" label="Esta semana" value={thisWeek} unit="eventos" />
                        <SummaryCard
                          icon={<FaShieldVirus size={18} color={risk.color}/>}
                          bg={risk.bg}
                          label="Score de risco"
                          value={risk.score}
                          unit={risk.level}
                          valueColor={risk.color}
                        />
                        <SummaryCard icon={<FaClock size={18} color="#0891b2"/>} bg="#ecfeff" label="Último acesso" value={userData.ultimo_acesso ? fmtRelative(userData.ultimo_acesso) : '—'} unit={userData.ultimo_acesso ? fmtDate(userData.ultimo_acesso) : 'Nunca'} small />
                      </div>

                      {/* ── Risk Assessment ────────────────────────────────── */}
                      <div style={{ ...S.card(risk.color), marginBottom:20 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom: risk.factors.length ? 14 : 0 }}>
                          {/* Gauge SVG */}
                          <RiskGauge score={risk.score} color={risk.color} />
                          <div>
                            <div style={{ fontSize:10,fontWeight:800,letterSpacing:1.5,textTransform:'uppercase',color:'#64748b',marginBottom:4 }}>Identity Risk Assessment</div>
                            <div style={{ fontSize:22,fontWeight:900,color:risk.color,letterSpacing:-.5 }}>{risk.level}</div>
                            <div style={{ fontSize:12,color:'#475569' }}>
                              {risk.score === 0 ? 'Nenhum fator de risco identificado.' : `${risk.factors.length} fator(es) de risco identificado(s).`}
                            </div>
                          </div>
                          <div style={{ marginLeft:'auto' }}>
                            <button type="button" style={S.btn('default')} onClick={fetchAudit}><FaSync size={11}/> Atualizar</button>
                          </div>
                        </div>
                        {risk.factors.length > 0 && (
                          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                            {risk.factors.map((f, i) => (
                              <div key={i} style={{
                                display:'flex', alignItems:'center', gap:10,
                                background:'#fff', borderRadius:8, padding:'8px 12px',
                                borderLeft:`3px solid ${f.severity==='HIGH'?'#dc2626':f.severity==='MEDIUM'?'#f59e0b':'#94a3b8'}`,
                              }}>
                                <f.icon size={13} color={f.severity==='HIGH'?'#dc2626':f.severity==='MEDIUM'?'#f59e0b':'#94a3b8'} />
                                <span style={{ fontSize:12,color:'#334155',flex:1 }}>{f.label}</span>
                                <span style={S.pill(
                                  f.severity==='HIGH'?'#dc2626':f.severity==='MEDIUM'?'#b45309':'#64748b',
                                  f.severity==='HIGH'?'#fff1f0':f.severity==='MEDIUM'?'#fff7ed':'#f8fafc',
                                )}>{f.severity}</span>
                                <span style={{ fontSize:11,fontWeight:800,color:'#94a3b8',minWidth:30,textAlign:'right' }}>+{f.pts}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* ── Mapa de Atividade ───────────────────────────────── */}
                      <div style={{ ...S.card('#475569'), marginBottom:20 }}>
                        <div style={S.cardLabel}><FaChartLine /> Mapa de Calor — Atividade por Dia e Hora</div>
                        {logs.length === 0 ? (
                          <div style={{ fontSize:12,color:'#94a3b8',padding:'16px 0',textAlign:'center' }}>Sem eventos para gerar o mapa de atividade.</div>
                        ) : (
                          <>
                            {/* Cabeçalho de horas */}
                            <div style={{ display:'flex', gap:2, marginBottom:4, paddingLeft:36 }}>
                              {[0,2,4,6,8,10,12,14,16,18,20,22].map(h => (
                                <div key={h} style={{ flex:'0 0 calc((100% / 12))', fontSize:9, color:'#94a3b8', textAlign:'center' }}>{h}h</div>
                              ))}
                            </div>
                            {/* Linhas por dia */}
                            {DAYS.map((dayName, dayIdx) => (
                              <div key={dayIdx} style={{ display:'flex', alignItems:'center', gap:2, marginBottom:3 }}>
                                <div style={{ width:32, fontSize:10, fontWeight:700, color:'#64748b', textAlign:'right', paddingRight:6, flexShrink:0 }}>{dayName}</div>
                                {heatMatrix[dayIdx].map((count, hour) => {
                                  const intensity = heatMax > 0 ? count / heatMax : 0;
                                  return (
                                    <div
                                      key={hour}
                                      title={`${dayName} ${hour}h — ${count} evento(s)`}
                                      style={{
                                        flex: 1,
                                        height: 14,
                                        borderRadius: 3,
                                        background: count === 0
                                          ? '#f1f5f9'
                                          : `rgba(59,130,246,${0.15 + intensity * 0.85})`,
                                        border: count > 0 ? '1px solid rgba(59,130,246,0.2)' : '1px solid #e2e8f0',
                                        cursor: count > 0 ? 'help' : 'default',
                                        transition: 'transform .1s',
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            ))}
                            {/* Legenda */}
                            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:12, justifyContent:'flex-end' }}>
                              <span style={{ fontSize:10, color:'#94a3b8' }}>Menos</span>
                              {[0, 0.25, 0.5, 0.75, 1].map((v, i) => (
                                <div key={i} style={{ width:14, height:14, borderRadius:3, background: v===0 ? '#f1f5f9' : `rgba(59,130,246,${0.15+v*0.85})`, border:'1px solid #e2e8f0' }} />
                              ))}
                              <span style={{ fontSize:10, color:'#94a3b8' }}>Mais</span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* ── Linha do Tempo ──────────────────────────────────── */}
                      <div style={{ ...S.card('#0f172a'), marginBottom: posthog?.found ? 20 : 0 }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                          <div style={S.cardLabel}><FaHistory /> Linha do Tempo — Últimos {Math.min(logs.length, 25)} Eventos</div>
                          {logs.length === 0 && <span style={{ fontSize:12, color:'#94a3b8' }}>Nenhum evento registrado.</span>}
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                          {logs.slice(0, 25).map((log, i) => {
                            const action = getAction(log.acao || '');
                            const Icon   = action.icon;
                            const isLast = i === Math.min(logs.length, 25) - 1;
                            return (
                              <div key={log.id || i} style={{ display:'flex', gap:12, position:'relative' }}>
                                {/* Linha vertical */}
                                {!isLast && (
                                  <div style={{ position:'absolute', left:15, top:28, bottom:0, width:2, background:'#e2e8f0', zIndex:0 }} />
                                )}
                                {/* Ícone */}
                                <div style={{
                                  width:32, height:32, borderRadius:16, flexShrink:0,
                                  background:action.bg, display:'flex', alignItems:'center', justifyContent:'center',
                                  border:`2px solid ${action.color}22`, position:'relative', zIndex:1,
                                }}>
                                  <Icon size={13} color={action.color} />
                                </div>
                                {/* Conteúdo */}
                                <div style={{ flex:1, paddingBottom:12 }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                                    <span style={{ fontSize:12,fontWeight:800,color:'#0f172a' }}>{action.label}</span>
                                    <span style={{ ...S.pill(action.color, action.bg), fontSize:9, padding:'1px 6px' }}>{log.acao}</span>
                                    <span style={{ fontSize:11,color:'#94a3b8',marginLeft:'auto' }}>{fmtRelative(log.created_at)}</span>
                                  </div>
                                  {log.detalhes && (
                                    <div style={{ fontSize:11,color:'#64748b',lineHeight:1.5, maxWidth:'100%', wordBreak:'break-word' }}>
                                      {log.detalhes.length > 120 ? log.detalhes.slice(0,120)+'…' : log.detalhes}
                                    </div>
                                  )}
                                  <div style={{ fontSize:10,color:'#cbd5e1',marginTop:2 }}>{fmtDate(log.created_at)}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* ── PostHog ─────────────────────────────────────────── */}
                      {posthog?.found && posthog.sessions?.length > 0 && (
                        <div style={{ ...S.card('#10b981'), marginTop: 0 }}>
                          <div style={S.cardLabel}><FaVideo /> Sessões de Gravação (PostHog)</div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:8 }}>
                            {posthog.sessions.map(s => (
                              <a key={s.id} href={s.url} target="_blank" rel="noreferrer" style={{
                                display:'block', padding:'10px 12px',
                                background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8,
                                textDecoration:'none', color:'inherit',
                              }}>
                                <div style={{ fontSize:11,fontWeight:800,color:'#065f46',marginBottom:4 }}>
                                  {new Date(s.startTime).toLocaleDateString('pt-BR')} — {Math.round(s.duration/60)}min
                                </div>
                                <div style={{ display:'flex', gap:12 }}>
                                  <span style={{ fontSize:11,color:'#047857' }}><FaMousePointer size={9} /> {s.clicks} cliques</span>
                                  <span style={{ fontSize:11,color:'#047857' }}><FaChartBar size={9} /> {s.pageviews} páginas</span>
                                </div>
                              </a>
                            ))}
                          </div>
                          {posthog.profileUrl && (
                            <a href={posthog.profileUrl} target="_blank" rel="noreferrer"
                              style={{ display:'inline-flex',alignItems:'center',gap:6,marginTop:10,fontSize:12,fontWeight:700,color:'#047857' }}>
                              <FaChartLine size={11}/> Ver perfil completo no PostHog
                            </a>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

          </div>

          <footer className="content-footer">
            <button className="btn-primary-large" onClick={handleSave} disabled={saving}>
              {saving ? 'SALVANDO…' : 'SALVAR ALTERAÇÕES'}
            </button>
          </footer>
        </main>
      </div>
    </div>
  );
}

/* ================================================================================================
 * Sub-componentes internos
 * ================================================================================================ */

/** Linha de atributo estilo AD (Identity Security Panel) */
function AttrRow({ label, value }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
      <span style={{ fontSize:12, color:'#64748b' }}>{label}</span>
      {value}
    </div>
  );
}

/** Card de resumo (Inteligência) */
function SummaryCard({ icon, bg, label, value, unit, valueColor = '#0f172a', small = false }) {
  return (
    <div style={{ background:bg, borderRadius:12, padding:'14px 16px', border:'1px solid #e2e8f0' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <div style={{ width:32,height:32,borderRadius:8,background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 1px 3px rgba(0,0,0,.06)' }}>{icon}</div>
        <span style={{ fontSize:10,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:1 }}>{label}</span>
      </div>
      <div style={{ fontSize: small ? 16 : 24, fontWeight:900, color:valueColor, letterSpacing:-.5, lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:10, color:'#94a3b8', marginTop:4 }}>{unit}</div>
    </div>
  );
}

/** Gauge de risco SVG semi-circular */
function RiskGauge({ score, color }) {
  const r   = 32;
  const cx  = 42;
  const cy  = 42;
  const strokeW = 6;
  // Semi-circle: ângulo de 180°, mapeamos score 0–100 para 0–180°
  const totalLen   = Math.PI * r; // semi-circle arc length
  const filledLen  = (score / 100) * totalLen;
  const emptyLen   = totalLen - filledLen;

  return (
    <svg width={84} height={52} viewBox="0 0 84 52" style={{ flexShrink:0 }}>
      {/* Track */}
      <path
        d={`M ${cx - r},${cy} A ${r},${r} 0 0,1 ${cx + r},${cy}`}
        fill="none" stroke="#e2e8f0" strokeWidth={strokeW} strokeLinecap="round"
      />
      {/* Fill */}
      <path
        d={`M ${cx - r},${cy} A ${r},${r} 0 0,1 ${cx + r},${cy}`}
        fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="round"
        strokeDasharray={`${filledLen} ${emptyLen}`}
        style={{ transition:'stroke-dasharray .6s ease' }}
      />
      {/* Score text */}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="14" fontWeight="900" fill={color}>{score}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize="8" fill="#94a3b8">/ 100</text>
    </svg>
  );
}

/** Linha de resumo de política */
function PolicyLine({ label, value }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:12 }}>
      <span style={{ color:'#94a3b8', fontWeight:600 }}>{label}</span>
      <span style={{ color:'#e2e8f0', fontWeight:700, textAlign:'right', maxWidth:260 }}>{value}</span>
    </div>
  );
}